<script setup>
import {computed, ref, watch} from 'vue';
import {useI18n} from 'vue-i18n';
import BondSelectModal from '@/components/modals/BondSelectModal.vue';
import BondDetailModal from '@/components/modals/BondDetailModal.vue';
import {bondDetailViewer} from '@/utils/features';
import {formatAmount, formatDate, formatNumber} from '@/helper';

/**
 * IMA-517 fáze 11a: sekce Dluhopisy v kroku Kalkulačka (ASO/SO), vždy viditelná pod strategií.
 *
 * Vybrané dluhopisy jsou karty se stepperem počtu kusů; poslední karta „Chci koupit dluhopis“ otevře výběr.
 * Pod kartami souhrn nominál / AÚV / celkem po měnách. Do grafu ani výnosu strategie se dluhopisy nepočítají.
 * S prvním dluhopisem se strategie Dluhopisy přidá automaticky (vznikne na BE při dokončení) – ručně ji vybrat nejde.
 *
 * Cena v kartě je orientační (počet × (nominál + AÚV za 1 ks k vybranému dni + poplatek)); přesnou částku spočítá BE
 * do Shrnutí. Den nákupu se vybírá z obchodních dnů dluhopisu (offer.available[].trade_dates); slevu z poplatku v %
 * zadává jen poradce (offer.can_set_fee_discount), klient pole nevidí a BE by slevu od klienta stejně zahodil.
 */
const props = defineProps({
  // data kroku: [{bond_id, quantity, trade_date, fee_discount_perc}]
  modelValue: {type: Array, default: () => []},
  // nabídka z kroku (BE getSteps): {available: [...], framework_contract_needed}
  offer: {type: Object, default: () => ({available: [], framework_contract_needed: false})},
  // problémy z BE (uložení kalkulačky / podpis Shrnutí): [{bond_id, code, requested, available}]
  problems: {type: Array, default: () => []},
  // dluhopis, na který se má upozornit po návratu ze Shrnutí
  highlightBondId: {type: Number, default: null},
});
// update:modelValue – nový výběr; validity – lze pokračovat (počty v limitech, dluhopisy dostupné)
const emit = defineEmits(['update:modelValue', 'validity']);

const {t} = useI18n();
const selectOpen = ref(false);
// detail dluhopisu z karty (stejný obsah jako stránka detailu, bez Koupit)
const {canShow: canShowDetail, advisor: detailAsAdvisor} = bondDetailViewer();
const detailBond = ref(null);

/** Údaje dluhopisu z nabídky podle id. */
function bondInfo(bondId) {
  return (props.offer?.available ?? []).find(bond => bond.id === Number(bondId)) ?? null;
}

/** Vybrané dluhopisy s údaji z nabídky a stavem karty. */
const rows = computed(() => props.modelValue.map(item => {
  const bond = bondInfo(item.bond_id);
  const quantity = Number(item.quantity) || 0;
  const min = bond?.limits?.min_quantity ?? 1;
  const max = bond?.limits?.can_buy ?? null;
  const problem = props.problems.find(p => p.bond_id === Number(item.bond_id)) ?? null;
  // obchodní dny: problém z BE nese aktuální nabídku (dny se mohly změnit), jinak nabídka z kroku
  const tradeDates = problem?.trade_dates ?? bond?.trade_dates ?? null;
  const tradeDate = item.trade_date || bond?.default_trade_date || tradeDates?.[0]?.date || null;
  const tradeDateValid = !tradeDates || tradeDates.some(day => day.date === tradeDate);
  // AÚV z ESO pro vybraný den; dluhopis bez AÚV z ESO má jen dopočet k nejbližšímu dni (orientační)
  const accruedPerPiece = tradeDates?.find(day => day.date === tradeDate)?.accrued_interest_per_piece ?? bond?.accrued_interest_per_piece ?? 0;
  const feePerc = Number(bond?.client_commission_perc ?? 0);
  const discount = Math.min(100, Math.max(0, Number(item.fee_discount_perc) || 0));
  const feePerPiece = Number(bond?.nominal_value ?? 0) * feePerc / 100 * (100 - discount) / 100;
  const perPiece = bond ? Number(bond.nominal_value ?? 0) + Number(accruedPerPiece) + feePerPiece : 0;
  // neplatná karta: dluhopis už nejde koupit, nebo počet mimo limity – nejde pokračovat
  let error = null;
  if (!bond || !bond.is_available) {
    error = t('onboardingBonds.error_not_available');
  } else if (quantity < min) {
    error = t('onboardingBonds.error_min', {count: formatNumber(min, 0)});
  } else if (max !== null && quantity > max) {
    error = t('onboardingBonds.error_max', {count: formatNumber(max, 0)});
  } else if (tradeDates && !tradeDates.length) {
    error = t('onboardingBonds.error_no_trade_date');
  } else if (!tradeDateValid || (problem?.code === 'INVALID_TRADE_DATE' && !item.trade_date)) {
    error = t('onboardingBonds.error_trade_date');
  } else if (problem && problem.code !== 'INVALID_TRADE_DATE' && problem.requested === quantity) {
    // BE odmítl přesně tento počet (mezitím ubyly kusy) – po změně počtu se hláška schová
    error = t('onboardingBonds.error_not_enough', {requested: formatNumber(problem.requested, 0), available: formatNumber(problem.available ?? 0, 0)});
  }

  return {
    bondId: Number(item.bond_id),
    bond,
    quantity,
    min,
    max,
    error,
    nominal: quantity * Number(bond?.nominal_value ?? 0),
    accrued: quantity * Number(accruedPerPiece),
    total: quantity * perPiece,
    tradeDates: tradeDates ?? [],
    tradeDate,
    feePerc,
    discount,
    fee: quantity * feePerPiece,
    // „Můžete zakoupit xy ks.“ zvýrazníme, když zbývá málo kusů
    lowAvailability: max !== null && max <= 3,
    saleEndsSoon: bond?.sale_to ? daysTo(bond.sale_to) <= 14 : false,
    saleEndsInDays: bond?.sale_to ? Math.max(0, daysTo(bond.sale_to)) : null,
  };
}));

const isValid = computed(() => rows.value.every(row => !row.error));
// rodič podle validity povolí „Pokračovat“; immediate – i pro uloženou kalkulačku po otevření
watch(isValid, value => emit('validity', value), {immediate: true});

/** Souhrn po měnách – dluhopisy mohou být v různých měnách. */
const totals = computed(() => {
  const byCurrency = {};
  for (const row of rows.value) {
    if (!row.bond) {
      continue;
    }
    const currency = row.bond.currency ?? 'CZK';
    byCurrency[currency] ??= {currency, nominal: 0, accrued: 0, total: 0};
    byCurrency[currency].nominal += row.nominal;
    byCurrency[currency].accrued += row.accrued;
    byCurrency[currency].total += row.total;
  }
  return Object.values(byCurrency);
});

const canAddMore = computed(() => (props.offer?.available ?? []).some(bond => bond.is_available && !props.modelValue.some(item => Number(item.bond_id) === bond.id)));

function daysTo(date) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((new Date(date + 'T00:00:00') - today) / 86400000);
}

/**
 * Text „končí za X dní“ ke konci prodeje se skloňováním (1 den, 2–4 dny, 0 a 5+ dní), dnes = „končí dnes“.
 * Je v řádku s datem, takže karta nezvětší výšku (štítek v záhlaví by karty vedle sebe rozhodil).
 */
function saleEndsText(days) {
  if (days === 0) {
    return t('onboardingBonds.sale_ends_today');
  }
  const key = days === 1 ? 'sale_ends_in_one' : (days >= 2 && days <= 4 ? 'sale_ends_in_few' : 'sale_ends_in_many');
  return t('onboardingBonds.' + key, {days});
}

function setQuantity(bondId, quantity) {
  emit('update:modelValue', props.modelValue.map(item => Number(item.bond_id) === bondId ? {...item, quantity: Math.max(0, Math.floor(Number(quantity) || 0))} : item));
}

/** Den nákupu karty – AÚV a cena se přepočítají podle dne. */
function setTradeDate(bondId, tradeDate) {
  emit('update:modelValue', props.modelValue.map(item => Number(item.bond_id) === bondId ? {...item, trade_date: tradeDate} : item));
}

/** Sleva z poplatku v % (jen poradce); mimo 0–100 se ořízne, desetinná čárka se převede na tečku. */
function setFeeDiscount(bondId, value) {
  const discount = Math.min(100, Math.max(0, Number(String(value).replace(',', '.')) || 0));
  emit('update:modelValue', props.modelValue.map(item => Number(item.bond_id) === bondId ? {...item, fee_discount_perc: discount} : item));
}

function changeQuantity(row, delta) {
  const next = row.quantity + delta;
  if (next < row.min || (row.max !== null && next > row.max)) {
    return;
  }
  setQuantity(row.bondId, next);
}

function remove(bondId) {
  emit('update:modelValue', props.modelValue.filter(item => Number(item.bond_id) !== bondId));
}

/** Otevře výběr dluhopisů – tlačítko „Přidat dluhopis“ je v záhlaví kalkulačky vedle „Přidat strategii“. */
function openSelect() {
  selectOpen.value = true;
}

defineExpose({openSelect, canAddMore});

/** Nově vybrané dluhopisy se přidají s minimálním počtem kusů. */
function add(ids) {
  selectOpen.value = false;
  const added = ids.map(id => ({
    bond_id: id,
    quantity: bondInfo(id)?.limits?.min_quantity ?? 1,
    // nejbližší možný den nákupu, bez slevy z poplatku
    trade_date: bondInfo(id)?.default_trade_date ?? null,
    fee_discount_perc: 0,
  }));
  emit('update:modelValue', [...props.modelValue, ...added]);
}
</script>

<template>
  <div class="mb-4">
  <div class="row">
    <div class="col-12 mb-2">
      <h5 class="mb-0">{{ t('onboardingBonds.title') }}</h5>
    </div>
    <div class="col-12">
      <small class="text-muted d-block mb-2">{{ t('onboardingBonds.strategy_added_text') }}</small>
      <div v-if="modelValue.length && offer?.framework_contract_needed" class="alert alert-light border py-2 small">{{ t('onboardingBonds.framework_contract') }}</div>
    </div>

    <!-- karty dluhopisů po dvou v řádku, ve stylu karet strategií -->
    <div v-for="row of rows" :key="row.bondId" class="col-12 col-md-6 mb-3">
      <div class="card h-100" :class="{'border-danger': row.error, 'border-warning': !row.error && highlightBondId === row.bondId}">
        <!-- záhlaví pevné výšky (název nejvýš na dva řádky), aby řádky karet vedle sebe lícovaly -->
        <div class="card-header d-flex justify-content-between align-items-start gap-2 bond-card__header">
          <div class="card-header__title bond-card__title" :title="row.bond?.name">{{ row.bond?.name ?? t('onboardingBonds.unknown_bond') }}</div>
          <div class="d-flex align-items-center gap-2 flex-shrink-0">
            <button v-if="canShowDetail && row.bond" type="button" class="btn btn-link p-0 text-decoration-none" @click="detailBond = row.bond">
              <i class="mdi mdi-eye"></i> {{ t('onboardingBonds.detail') }}
            </button>
            <i class="btn-close cursor-pointer" @click="remove(row.bondId)" :aria-label="t('onboardingBonds.remove')"></i>
          </div>
        </div>
        <div class="card-body info-table">
          <template v-if="row.bond">
            <div class="info-table__row row">
              <span class="info-table__label col-6">{{ t('onboardingBonds.coupon') }}</span>
              <span class="info-table__value col-6 text-end">{{ row.bond.coupon_rate === null ? '---' : formatNumber(row.bond.coupon_rate) + ' % p.a.' }}</span>
            </div>
            <div class="info-table__row row">
              <span class="info-table__label col-6">{{ t('onboardingBonds.coupon_frequency') }}</span>
              <span class="info-table__value col-6 text-end">{{ row.bond.coupon_frequency_label ?? '---' }}</span>
            </div>
            <div class="info-table__row row">
              <span class="info-table__label col-6">{{ t('onboardingBonds.maturity') }}</span>
              <span class="info-table__value col-6 text-end">{{ row.bond.maturity_date ? formatDate(row.bond.maturity_date, 'd.m.Y') : '---' }}</span>
            </div>
            <div class="info-table__row row">
              <span class="info-table__label col-6">{{ t('onboardingBonds.price_per_piece') }}</span>
              <span class="info-table__value col-6 text-end">{{ formatAmount(row.bond.nominal_value, row.bond.currency, 0) }}</span>
            </div>
            <div class="info-table__row row">
              <span class="info-table__label col-6">ISIN</span>
              <span class="info-table__value col-6 text-end">{{ row.bond.isin ?? '---' }}</span>
            </div>
            <!-- den nákupu z obchodních dnů; s jediným dnem jen informace -->
            <div v-if="row.tradeDates.length" class="info-table__row row align-items-center">
              <label class="info-table__label col-6" :for="'bond-trade-date-' + row.bondId">{{ t('onboardingBonds.trade_date') }}</label>
              <div class="info-table__value col-6 text-end">
                <select v-if="row.tradeDates.length > 1" :id="'bond-trade-date-' + row.bondId" :value="row.tradeDate" class="form-select form-select-sm d-inline-block w-auto"
                        @change="setTradeDate(row.bondId, $event.target.value)">
                  <option v-for="day of row.tradeDates" :key="day.date" :value="day.date">{{ formatDate(day.date, 'd.m.Y') }}</option>
                </select>
                <span v-else :id="'bond-trade-date-' + row.bondId">{{ formatDate(row.tradeDates[0].date, 'd.m.Y') }}</span>
              </div>
            </div>
            <!-- konec prodeje je řádek v každé kartě (dřív štítek v záhlaví, který kartu zvětšil) -->
            <div class="info-table__row row">
              <span class="info-table__label col-6">{{ t('onboardingBonds.sale_to') }}</span>
              <span class="info-table__value col-6 text-end" :class="{'text-warning fw-bold': row.saleEndsSoon}">
                {{ row.bond.sale_to ? formatDate(row.bond.sale_to, 'd.m.Y') : '---' }}
                <span v-if="row.saleEndsSoon" class="d-block small">{{ saleEndsText(row.saleEndsInDays) }}</span>
              </span>
            </div>
            <!-- počet kusů vpravo, pod ním „Můžete zakoupit xy ks.“ -->
            <div class="info-table__row row align-items-start">
              <label class="info-table__label col-5 pt-2" :for="'bond-quantity-' + row.bondId">{{ t('onboardingBonds.quantity') }}</label>
              <div class="col-7 d-flex flex-column align-items-end">
                <div class="input-group" style="max-width: 170px;">
                  <button class="btn btn-outline-tenant" type="button" :disabled="row.quantity <= row.min" @click="changeQuantity(row, -1)">−</button>
                  <input :id="'bond-quantity-' + row.bondId" :value="row.quantity" type="number" class="form-control text-center" :min="row.min" :max="row.max ?? undefined" step="1"
                         @input="setQuantity(row.bondId, $event.target.value)">
                  <button class="btn btn-outline-tenant" type="button" :disabled="row.max !== null && row.quantity >= row.max" @click="changeQuantity(row, 1)">+</button>
                </div>
                <!-- řádek „Můžete zakoupit“ drží místo i bez omezení kusů, aby měly karty stejnou výšku -->
                <small class="mt-1 bond-card__can-buy" :class="row.lowAvailability ? 'text-warning fw-bold' : 'text-muted'">{{ row.max !== null ? t('onboardingBonds.can_buy', {count: formatNumber(row.max, 0)}) : '' }}</small>
              </div>
            </div>
            <!-- poplatek z ESO v %; slevu z něj zadává jen poradce -->
            <template v-if="row.feePerc">
              <div class="info-table__row row">
                <span class="info-table__label col-6">{{ t('onboardingBonds.fee') }} ({{ formatNumber(row.feePerc) }} %)</span>
                <span class="info-table__value col-6 text-end">{{ formatAmount(row.fee, row.bond.currency) }}</span>
              </div>
              <div v-if="offer?.can_set_fee_discount" class="info-table__row row align-items-center">
                <label class="info-table__label col-6" :for="'bond-fee-discount-' + row.bondId">{{ t('onboardingBonds.fee_discount') }}</label>
                <div class="col-6 d-flex justify-content-end">
                  <input :id="'bond-fee-discount-' + row.bondId" :value="row.discount" type="number" min="0" max="100" step="0.01"
                         class="form-control form-control-sm text-end" style="max-width: 6rem;" @change="setFeeDiscount(row.bondId, $event.target.value)">
                </div>
              </div>
            </template>
          </template>
          <div v-if="row.error" class="text-danger small mt-2">{{ row.error }}</div>
        </div>
        <div v-if="row.bond" class="card-footer d-flex justify-content-between fw-bold">
          <span>{{ t('onboardingBonds.total_with_accrued') }}</span>
          <span class="bond-card__total">{{ formatAmount(row.total, row.bond.currency) }}</span>
        </div>
      </div>
    </div>

    <!-- dluhopisy se přidávají tlačítkem „Přidat dluhopis“ v záhlaví kalkulačky (openSelect) -->
    <div v-if="!rows.length" class="col-12 mb-3">
      <small class="text-muted">{{ t('onboardingBonds.no_bonds_selected') }}</small>
    </div>

  </div>

  <!-- souhrn dluhopisů jako dlaždice (po měnách) – vlastní řádek, ne vedle karty „Chci koupit dluhopis“ -->
  <div class="row">
    <template v-for="total of totals" :key="total.currency">
      <div class="col-12 col-md-4 mb-3">
        <div class="card card--big-value card--ns h-100">
          <div class="card-body">
            <div class="card__value__label">{{ t('onboardingBonds.sum_nominal') }}</div>
            <div class="card__value">{{ formatAmount(total.nominal, total.currency) }}</div>
          </div>
        </div>
      </div>
      <div class="col-12 col-md-4 mb-3">
        <div class="card card--big-value card--ns h-100">
          <div class="card-body">
            <div class="card__value__label">{{ t('onboardingBonds.sum_accrued') }}</div>
            <div class="card__value">{{ formatAmount(total.accrued, total.currency) }}</div>
          </div>
        </div>
      </div>
      <div class="col-12 col-md-4 mb-3">
        <div class="card card--big-value card--ns h-100">
          <div class="card-body">
            <div class="card__value__label">{{ t('onboardingBonds.sum_total') }}</div>
            <div class="card__value">{{ formatAmount(total.total, total.currency) }}</div>
          </div>
        </div>
      </div>
    </template>
    <div v-if="totals.length" class="col-12">
      <small class="text-muted">{{ t('onboardingBonds.indicative_note') }}</small>
    </div>

  </div>

    <BondDetailModal v-if="detailBond" :bond="detailBond" :advisor="detailAsAdvisor" @close="detailBond = null"/>
    <BondSelectModal v-if="selectOpen" :bonds="offer?.available ?? []" :selected-ids="modelValue.map(item => Number(item.bond_id))"
                     @confirm="add" @cancel="selectOpen = false"/>
  </div>
</template>

<style scoped>
/* barva aplikace (tenant) – bootstrapová text-primary má jinou modrou */
.bond-card__total {
  color: var(--primaryColor);
}

.bond-card__header {
  min-height: 4.25rem;
}

/* název nejvýš na dva řádky, delší se zkrátí – záhlaví karet má stejnou výšku */
.bond-card__title {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.bond-card__can-buy {
  min-height: 1.25rem;
}
</style>
